/**
 * TanStack Query hooks for `multi-currency`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	refreshAll,
	remove,
	update,
	multiCurrencyQueryKeys,
	type CreateCurrencyBody,
	type CurrencyRow,
	type ListCurrencyResponse,
	type RefreshRatesResponse,
	type UpdateCurrencyBody,
} from "./api";

export function useMultiCurrencyList(): UseQueryResult<ListCurrencyResponse, Error> {
	return useQuery({
		queryKey: multiCurrencyQueryKeys.list(),
		queryFn: list,
	});
}

export function useMultiCurrencyCreate(): UseMutationResult<
	CurrencyRow,
	Error,
	CreateCurrencyBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: multiCurrencyQueryKeys.all });
		},
	});
}

export function useMultiCurrencyUpdate(): UseMutationResult<
	CurrencyRow,
	Error,
	{ id: string; patch: UpdateCurrencyBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, patch }) => update(id, patch),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: multiCurrencyQueryKeys.all });
		},
	});
}

export function useMultiCurrencyDelete(): UseMutationResult<
	{ removed: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: multiCurrencyQueryKeys.all });
		},
	});
}

export function useMultiCurrencyRefreshAll(): UseMutationResult<
	RefreshRatesResponse,
	Error,
	void
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: refreshAll,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: multiCurrencyQueryKeys.all });
		},
	});
}
